<script setup>
/* global axios */
// Notificações do super admin (custom/app/models/neuxs/notificacao.rb): uma
// faixa por aviso no topo do painel, até a pessoa fechar. "Fechei" vai para o
// servidor, que conta uma vez por dia por pessoa — em qualquer computador.
//
// Texto puro: título e mensagem entram como texto (nada de v-html), e o link só
// chega aqui se for https (validado no servidor e numa CHECK do banco).
import { ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Banner from 'dashboard/components-next/banner/Banner.vue';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
});

const { t } = useI18n({
  messages: {
    pt_BR: { NEUXS_AVISOS: { FECHAR: 'Fechar' } },
    en: { NEUXS_AVISOS: { FECHAR: 'Fechar' } },
  },
});

const COR = { info: 'blue', aviso: 'amber', urgente: 'ruby' };
// Um aviso novo aparece sem recarregar a página, mas sem martelar o servidor.
const INTERVALO = 15 * 60 * 1000;

const avisos = ref([]);
const fechando = ref(null);
let relogio = null;

const base = () => `/api/v1/accounts/${props.accountId}/neuxs/notificacoes`;

const busca = async () => {
  try {
    const { data } = await axios.get(base());
    avisos.value = data.avisos || [];
  } catch {
    // Aviso é acessório: falhar aqui não pode atrapalhar o atendimento.
    avisos.value = [];
  }
};

const fecha = async aviso => {
  fechando.value = aviso.id;
  try {
    await axios.post(`${base()}/${aviso.id}/visto`);
  } catch {
    // Sem registro, ele volta no próximo carregamento — melhor que não sumir.
  } finally {
    avisos.value = avisos.value.filter(a => a.id !== aviso.id);
    fechando.value = null;
  }
};

watch(
  () => props.accountId,
  () => {
    busca();
    clearInterval(relogio);
    relogio = setInterval(busca, INTERVALO);
  },
  { immediate: true }
);

onBeforeUnmount(() => clearInterval(relogio));
</script>

<template>
  <Banner
    v-for="aviso in avisos"
    :key="aviso.id"
    :color="COR[aviso.tom] || 'slate'"
    :action-label="t('NEUXS_AVISOS.FECHAR')"
    :is-loading="fechando === aviso.id"
    class="!rounded-none !justify-center"
    @action="fecha(aviso)"
  >
    <span class="text-xs">
      <strong>{{ aviso.titulo }}</strong>
      <span class="whitespace-pre-line"> {{ aviso.mensagem }}</span>
      <a
        v-if="aviso.link_url"
        :href="aviso.link_url"
        target="_blank"
        rel="noopener noreferrer"
        class="ml-1 underline font-medium"
        @click="fecha(aviso)"
      >
        {{ aviso.link_rotulo }}
      </a>
    </span>
  </Banner>
</template>
